import { useApiPost } from '@/api/main/query'
import { TOKEN_KEY, USER_ROLE_KEY } from '@/features/auth'
import { authAPI } from '@/features/auth/api/endpoints'
import { ThemeToggle } from '@/features/themeToggle/ui/ThemeToggle'
import { BaseButton } from '@/shared/components/BaseButton/BaseButton'
import { BaseInput } from '@/shared/components/BaseInput/BaseInput'
import { PasswordInput } from '@/shared/components/PasswordInput/PasswordInput'
import MainIcon from '@/shared/components/icons/MainIcon'
import { passwordValidation } from '@/shared/constants/validationRules'
import { useToast } from '@/shared/hooks'
import { setStorageItem } from '@/shared/lib/storage/localStorage'
import { useSupportContact } from '@/entities/domain/hooks/useSupportContact'
import { FieldError, FieldValues, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useState } from 'react'

const Login = () => {
  const {
    register,
    handleSubmit,
    reset,
    getValues,
    formState: { errors },
  } = useForm()

  const { addToast } = useToast()
  const navigate = useNavigate()
  const { telegramUrl, displayName } = useSupportContact()
  const [has2FA, setHas2FA] = useState(false)
  const [token2FA, setToken2FA] = useState('')

  interface UserData {
    role: string
    token: string
    expiresAtUtc: string
  }

  interface sendData {
    email: string
    password: string
    token2FA?: string
  }

  const { mutate: signin, isPending } = useApiPost<UserData, sendData>(authAPI.signin, {
    onSuccess: (value) => {
      if (value?.token) {
        setStorageItem(TOKEN_KEY, value.token)
        setStorageItem(USER_ROLE_KEY, value.role)
        navigate('/dashboard', { replace: true })
      } else {
        addToast('Ошибка!', 'Ошибка регистрации: сервер не вернул токен', 'error')
      }
      reset()
      setHas2FA(false)
      setToken2FA('')
    },
    onError: (error) => {
      if (error.message.includes('Ошибка токена 2fa')) {
        if (has2FA) {
          addToast('Ошибка!', 'Неверный код', 'error')
          setToken2FA('')
        } else setHas2FA(true)
        return
      }
      addToast('Ошибка!', error.message || 'Ошибка регистрации', 'error')
    },
  })

  const onSubmit = async (data: FieldValues) => {
    if (data.password !== data.confirmPassword) {
      addToast('Ошибка!', 'Пароли не совпадают', 'error')
      return
    }
    const { email, loginPassword } = data
    signin({ email, password: loginPassword, ...(has2FA ? { token2FA } : {}) })
  }

  const toRegister = () => {
    navigate('/register')
  }

  const toLogin = () => {
    setHas2FA(false)
    setToken2FA('')
    reset({
      email: '',
      loginPassword: '',
    })
  }

  return (
    <div className="flex flex-col items-center gap-y-20 w-full max-w-[350px]">
      <ThemeToggle />
      <div className="flex justify-between items-center w-full">
        <MainIcon />

        <h2 className="text-text-secondary dark:text-primary text-xl">Авторизация</h2>
      </div>
      <div className="w-full">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col space-y-4">
            {!has2FA ? (
              <>
                <BaseInput
                  placeholder="Почта"
                  {...register('email', {
                    required: 'Поле обязательно',
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: 'Некорректный email',
                    },
                  })}
                  error={(errors.email as FieldError)?.message}
                  size="md"
                />
                <PasswordInput
                  placeholder="Пароль"
                  {...register('loginPassword', passwordValidation)}
                  error={(errors.loginPassword as FieldError)?.message}
                  size="md"
                />

                <div className="flex justify-center items-center mb-4">
                  <a href="#">
                    <span className="text-primary dark:text-primary-dark text-sm">
                      Забыли пароль?
                    </span>
                  </a>
                </div>
                <BaseButton type="submit" padding="lg" isLoading={isPending}>
                  Войти
                </BaseButton>
                <BaseButton variant="outlined" padding="lg" onClick={toRegister}>
                  Зарегистрироваться
                </BaseButton>
              </>
            ) : (
              <>
                <div className="text-center">
                  <p className="text-sm text-text-secondary">Введите код из Google Authenticator</p>
                  <p className="text-xs text-text-secondary mt-1">
                    Код обновляется каждые 30 секунд
                  </p>
                </div>
                <BaseInput
                  placeholder="Введите 6-значный код из приложения"
                  value={token2FA}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '')
                    if (value.length <= 6) setToken2FA(value)
                    if (value.length === 6) {
                      const { email, loginPassword } = getValues()
                      signin({ email, password: loginPassword, token2FA: value })
                    }
                  }}
                  maxLength={6}
                />
                <BaseButton
                  type="submit"
                  padding="lg"
                  isLoading={isPending}
                  disabled={has2FA && token2FA.length !== 6}
                >
                  Войти
                </BaseButton>
                <BaseButton variant="outlined" padding="lg" onClick={toLogin}>
                  Назад
                </BaseButton>
              </>
            )}
          </div>
        </form>
      </div>
      <div className="text-center">
        <span className="text-text-main dark:text-text-secondary text-sm">
          Контакт поддержки:
          <a href={telegramUrl} target="_blank" rel="noopener noreferrer">
            <span className="text-primary dark:text-primary-dark text-sm">{displayName}</span>
          </a>
        </span>
      </div>
    </div>
  )
}

export default Login
